import React from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CircularProgress, Grid } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { BaseField, StyledButton } from 'shared'
import { verifyUpdatedEmail } from 'api/auth'
import { useAuth } from 'utils/providers/AuthProvider'

const validationSchema = yup.object().shape({
  code: yup.string().required('Verification code is required'),
})

type FormValues = { code: string }

type Props = {
  newEmail: string
  closeModal: () => void
}

const VerifyCodeStep = ({ newEmail, closeModal }: Props) => {
  const { logout } = useAuth()

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: { code: '' },
  })

  const { isLoading, mutate } = useMutation({
    mutationFn: verifyUpdatedEmail,
    onSuccess: () => {
      closeModal()
      logout()
      toast.success('Please log in with the new email')
    },
    onError: () => {
      toast.error('Invalid or expired verification code. Please try again.')
    },
  })

  const onSubmit = handleSubmit((values) => {
    mutate({ email: newEmail, code: values.code })
  })

  return (
    <form onSubmit={onSubmit}>
      <Controller
        control={control}
        name="code"
        render={({ field, fieldState }) => (
          <BaseField
            name=""
            value={field.value}
            label="Verification code"
            placeholder="Enter verification code"
            error={fieldState.invalid}
            helperText={fieldState.error?.message}
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
      <Grid
        mt={2}
        gap={2}
        container
        alignItems="center"
        justifyContent="flex-end"
      >
        <StyledButton variant="outlined" onClick={closeModal}>
          Cancel
        </StyledButton>
        <StyledButton
          type="submit"
          variant="contained"
          disabled={!isDirty || !isValid || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress sx={{ color: 'white' }} size="1em" />
            ) : null
          }
        >
          Verify
        </StyledButton>
      </Grid>
    </form>
  )
}

export default VerifyCodeStep
